/**
 * AdminTemplatesTab — prompt template editor.
 *
 * Three-layer override system:
 *   defaults → org overrides → user overrides (last wins)
 *
 * Per key, admin can also override: model, reasoning_effort, temperature.
 * Export/import full settings bundle as JSON.
 */

import { type ChangeEvent, useCallback, useEffect, useState } from "react";
import { authFetchRaw, authFetchJson } from "@/lib/api";

interface PromptKey {
  key: string;
  label?: string;
  description?: string;
  params?: Record<string, unknown>;
}

interface PromptTemplatesResponse {
  defaults: Record<string, string>;
  org: Record<string, string | Record<string, unknown>>;
  user: Record<string, string | Record<string, unknown>>;
  keys: PromptKey[];
  available_models: string[];
  reasoning_options: string[];
}

interface ParamOverride {
  model?: string;
  reasoning?: string;
  temperature?: number;
}

type TargetType = "org" | "user";

export function AdminTemplatesTab() {
  const [target, setTarget] = useState<TargetType>("org");
  const [targetId, setTargetId] = useState("");
  const [defaults, setDefaults] = useState<Record<string, string>>({});
  const [edits, setEdits] = useState<Record<string, string>>({});
  const [paramEdits, setParamEdits] = useState<Record<string, ParamOverride>>({});
  const [keys, setKeys] = useState<PromptKey[]>([]);
  const [availableModels, setAvailableModels] = useState<string[]>([]);
  const [reasoningOptions, setReasoningOptions] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [expandedKey, setExpandedKey] = useState<string | null>(null);

  const loadTemplates = useCallback(async () => {
    if (!targetId) return;
    setLoading(true);
    setError("");
    try {
      const params = target === "org" ? `org_id=${targetId}` : `user_id=${targetId}`;
      const data = await authFetchJson<PromptTemplatesResponse>(
        `/api/admin/prompt-templates?${params}`,
      );
      setDefaults(data.defaults ?? {});
      setKeys(data.keys ?? []);
      setAvailableModels(data.available_models ?? []);
      setReasoningOptions(data.reasoning_options ?? []);

      // Initialize edits: user override > org override > default
      const initialEdits: Record<string, string> = {};
      const initialParams: Record<string, ParamOverride> = {};
      const userOverrides = data.user ?? {};
      const orgOverrides = data.org ?? {};
      for (const key of Object.keys(data.defaults ?? {})) {
        if (target === "user" && typeof userOverrides[key] === "string") {
          initialEdits[key] = userOverrides[key] as string;
        } else if (target === "org" && typeof orgOverrides[key] === "string") {
          initialEdits[key] = orgOverrides[key] as string;
        } else {
          initialEdits[key] = data.defaults?.[key] ?? "";
        }
      }
      // Param overrides
      for (const pk of data.keys ?? []) {
        const dParams = (pk.params ?? {}) as ParamOverride;
        const oParams = (orgOverrides[`${pk.key}__params`] ?? {}) as ParamOverride;
        const uParams = (userOverrides[`${pk.key}__params`] ?? {}) as ParamOverride;
        initialParams[pk.key] = { ...dParams, ...oParams, ...uParams };
      }
      setEdits(initialEdits);
      setParamEdits(initialParams);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Laden mislukt.");
    } finally {
      setLoading(false);
    }
  }, [target, targetId]);

  useEffect(() => {
    if (targetId) void loadTemplates();
  }, [targetId, loadTemplates]);

  const savePromptTemplates = useCallback(async () => {
    if (!targetId) return;
    setSaving(true);
    setError("");
    setSuccessMsg("");
    try {
      const url =
        target === "org"
          ? `/api/admin/prompt-templates/org/${targetId}`
          : `/api/admin/prompt-templates/user/${targetId}`;
      const overrides: Record<string, unknown> = {};
      for (const [key, value] of Object.entries(edits)) {
        if (value !== defaults[key]) {
          overrides[key] = value;
        }
      }
      for (const pk of keys) {
        const defaultParams = (pk.params ?? {}) as Record<string, unknown>;
        const current = paramEdits[pk.key] ?? {};
        if (JSON.stringify(current) !== JSON.stringify(defaultParams)) {
          overrides[`${pk.key}__params`] = current;
        }
      }
      await authFetchJson(url, {
        method: "PUT",
        body: JSON.stringify(overrides),
      });
      setSuccessMsg("Opgeslagen");
      setTimeout(() => setSuccessMsg(""), 2000);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Opslaan mislukt.");
    } finally {
      setSaving(false);
    }
  }, [target, targetId, edits, paramEdits, defaults, keys]);

  const resetKey = useCallback(
    (key: string) => {
      setEdits((prev) => ({ ...prev, [key]: defaults[key] ?? "" }));
    },
    [defaults],
  );

  const isModified = useCallback(
    (key: string) => edits[key] !== defaults[key],
    [edits, defaults],
  );

  const exportSettings = useCallback(async () => {
    if (!targetId) return;
    try {
      const params = target === "org" ? `org_id=${targetId}` : `user_id=${targetId}`;
      const resp = await authFetchRaw(`/api/admin/settings-export?${params}`);
      if (!resp.ok) throw new Error("Export mislukt.");
      const data = (await resp.json()) as { org?: { name?: string } };
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const targetName = target === "org" ? data.org?.name ?? "org" : "user";
      a.download = `carmen-settings-${targetName}-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Export mislukt.");
    }
  }, [target, targetId]);

  const importSettings = useCallback(
    async (e: ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file || !targetId) return;
      try {
        const text = await file.text();
        const data = JSON.parse(text) as { _meta?: Record<string, unknown> };
        data._meta = {
          ...(data._meta ?? {}),
          target_type: target,
          target_id: targetId,
        };
        await authFetchJson("/api/admin/settings-import", {
          method: "POST",
          body: JSON.stringify(data),
        });
        setSuccessMsg("Instellingen geïmporteerd");
        await loadTemplates();
      } catch (err) {
        setError(err instanceof Error ? err.message : "Import mislukt.");
      } finally {
        if (e.target) e.target.value = "";
      }
    },
    [target, targetId, loadTemplates],
  );

  return (
    <div className="space-y-4">
      {/* Target selector */}
      <div className="flex flex-wrap items-center gap-3 rounded-2xl border border-[rgba(10,52,96,0.10)] bg-white p-4 shadow-sm">
        <div className="flex rounded-lg border border-[rgba(10,52,96,0.10)] p-0.5">
          {(["org", "user"] as TargetType[]).map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => setTarget(t)}
              className={`px-3 py-1 text-xs font-medium ${
                target === t ? "rounded bg-rose-500 text-white" : "text-[rgba(10,52,96,0.78)]"
              }`}
            >
              {t === "org" ? "Organisatie" : "Gebruiker"}
            </button>
          ))}
        </div>
        <input
          type="text"
          value={targetId}
          onChange={(e) => setTargetId(e.target.value)}
          placeholder={target === "org" ? "Org ID (UUID)" : "User ID (UUID)"}
          className="flex-1 rounded-lg border border-[rgba(10,52,96,0.18)] px-3 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-[var(--cs-rose)]"
        />
        <button
          type="button"
          onClick={() => void loadTemplates()}
          disabled={!targetId || loading}
          className="rounded-lg bg-rose-500 px-3 py-1.5 text-xs font-semibold text-white hover:bg-rose-600 disabled:opacity-50"
        >
          {loading ? "Laden…" : "Laad"}
        </button>
        <button
          type="button"
          onClick={() => void exportSettings()}
          disabled={!targetId}
          className="rounded-lg border border-[rgba(10,52,96,0.10)] bg-white px-3 py-1.5 text-xs text-[var(--cs-navy)] hover:bg-[var(--cs-ivory)] disabled:opacity-50"
        >
          Export
        </button>
        <label className="cursor-pointer rounded-lg border border-[rgba(10,52,96,0.10)] bg-white px-3 py-1.5 text-xs text-[var(--cs-navy)] hover:bg-[var(--cs-ivory)]">
          Import
          <input
            type="file"
            accept=".json"
            onChange={(e) => void importSettings(e)}
            className="hidden"
          />
        </label>
        {saving ? <span className="text-xs text-[var(--cs-ink-subtle)]">Opslaan…</span> : null}
        {successMsg ? (
          <span className="rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700">
            ✓ {successMsg}
          </span>
        ) : null}
      </div>

      {error ? (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          {error}
        </div>
      ) : null}

      {/* Prompt keys */}
      {keys.length > 0 ? (
        <div className="space-y-2">
          {keys.map((pk) => {
            const expanded = expandedKey === pk.key;
            const modified = isModified(pk.key);
            return (
              <div
                key={pk.key}
                className={`rounded-2xl border glass-card transition-all ${
                  modified ? "border-amber-300" : "border-[rgba(10,52,96,0.10)]"
                }`}
              >
                <button
                  type="button"
                  onClick={() => setExpandedKey(expanded ? null : pk.key)}
                  className="flex w-full items-center gap-3 px-4 py-3 text-left"
                >
                  <span className="text-xs font-mono font-semibold text-[var(--cs-navy)]">
                    {pk.key}
                  </span>
                  {pk.label ? (
                    <span className="text-xs text-[var(--cs-ink-muted)]">{pk.label}</span>
                  ) : null}
                  {modified ? (
                    <span className="rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-700">
                      gewijzigd
                    </span>
                  ) : null}
                  <svg
                    className={`ml-auto h-3.5 w-3.5 text-[var(--cs-ink-subtle)] transition-transform ${expanded ? "rotate-180" : ""}`}
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                      clipRule="evenodd"
                    />
                  </svg>
                </button>
                {expanded ? (
                  <div className="space-y-3 border-t border-slate-100 px-4 py-4">
                    {pk.description ? (
                      <p className="text-xs text-[var(--cs-ink-muted)]">{pk.description}</p>
                    ) : null}
                    <div>
                      <div className="mb-1 flex items-center justify-between">
                        <label
                          htmlFor={`prompt-${pk.key}`}
                          className="text-[10px] font-semibold uppercase tracking-wider text-[var(--cs-ink-muted)]"
                        >
                          Prompt tekst
                        </label>
                        {modified ? (
                          <button
                            type="button"
                            onClick={() => resetKey(pk.key)}
                            className="text-[10px] text-rose-600 hover:underline"
                          >
                            Reset naar default
                          </button>
                        ) : null}
                      </div>
                      <textarea
                        id={`prompt-${pk.key}`}
                        value={edits[pk.key] ?? ""}
                        onChange={(e) =>
                          setEdits((prev) => ({ ...prev, [pk.key]: e.target.value }))
                        }
                        rows={8}
                        className="w-full rounded-lg border border-[rgba(10,52,96,0.18)] p-2 font-mono text-[11px] focus:outline-none focus:ring-2 focus:ring-[var(--cs-rose)]"
                      />
                    </div>
                    <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                      <div>
                        <label className="text-[10px] font-semibold uppercase tracking-wider text-[var(--cs-ink-muted)]">
                          Model
                        </label>
                        <select
                          value={paramEdits[pk.key]?.model ?? ""}
                          onChange={(e) =>
                            setParamEdits((prev) => ({
                              ...prev,
                              [pk.key]: { ...prev[pk.key], model: e.target.value },
                            }))
                          }
                          className="mt-1 w-full rounded border border-[rgba(10,52,96,0.18)] px-2 py-1 text-xs"
                        >
                          <option value="">(default)</option>
                          {availableModels.map((m) => (
                            <option key={m} value={m}>
                              {m}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="text-[10px] font-semibold uppercase tracking-wider text-[var(--cs-ink-muted)]">
                          Reasoning
                        </label>
                        <select
                          value={paramEdits[pk.key]?.reasoning ?? ""}
                          onChange={(e) =>
                            setParamEdits((prev) => ({
                              ...prev,
                              [pk.key]: { ...prev[pk.key], reasoning: e.target.value },
                            }))
                          }
                          className="mt-1 w-full rounded border border-[rgba(10,52,96,0.18)] px-2 py-1 text-xs"
                        >
                          <option value="">(default)</option>
                          {reasoningOptions.map((r) => (
                            <option key={r} value={r}>
                              {r}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="text-[10px] font-semibold uppercase tracking-wider text-[var(--cs-ink-muted)]">
                          Temperature
                        </label>
                        <input
                          type="number"
                          step="0.1"
                          min="0"
                          max="2"
                          value={paramEdits[pk.key]?.temperature ?? ""}
                          onChange={(e) =>
                            setParamEdits((prev) => ({
                              ...prev,
                              [pk.key]: {
                                ...prev[pk.key],
                                temperature: e.target.value === "" ? undefined : Number(e.target.value),
                              },
                            }))
                          }
                          className="mt-1 w-full rounded border border-[rgba(10,52,96,0.18)] px-2 py-1 text-xs"
                          placeholder="(default)"
                        />
                      </div>
                    </div>
                  </div>
                ) : null}
              </div>
            );
          })}

          <div className="flex justify-end pt-2">
            <button
              type="button"
              onClick={() => void savePromptTemplates()}
              disabled={saving || !targetId}
              className="rounded-xl bg-rose-500 px-4 py-2 text-sm font-semibold text-white hover:bg-rose-600 disabled:opacity-50"
            >
              {saving ? "Opslaan…" : "Alle wijzigingen opslaan"}
            </button>
          </div>
        </div>
      ) : targetId ? (
        <p className="text-sm text-[var(--cs-ink-muted)]">Geen templates geladen.</p>
      ) : (
        <p className="text-sm text-[var(--cs-ink-muted)]">Selecteer een organisatie of gebruiker.</p>
      )}
    </div>
  );
}
